import { create } from 'zustand';

/**
 * Deliberate trade-off (launch-day auth friction fix, quick-260817-fly):
 * secretKey/publicKey persist to per-tab sessionStorage so ONE unlock lasts
 * the life of the tab — matching the extension's chrome.storage.session
 * behaviour — and are gone when the tab closes. This was an explicit founder
 * UX decision ("one login, don't ask again until the tab or application is
 * actually closed"). XSS exfiltration risk is mitigated by the nginx CSP.
 * channelKeys stay memory-only and are cleared on logout or page refresh.
 */
const SESSION_KEY_SECRET = 'clinikchat_e2ee_secret';
const SESSION_KEY_PUBLIC = 'clinikchat_e2ee_public';

/** Guarded read — returns null if sessionStorage is unavailable/blocked. */
function readSessionKey(name: string): string | null {
  try {
    return window.sessionStorage.getItem(name);
  } catch {
    return null;
  }
}

function writeSessionKey(name: string, value: string): void {
  try {
    window.sessionStorage.setItem(name, value);
  } catch {
    // Storage unavailable (private mode/quota/policy) — memory-only fallback.
  }
}

function removeSessionKey(name: string): void {
  try {
    window.sessionStorage.removeItem(name);
  } catch {
    // Nothing to remove if storage is unavailable.
  }
}

/**
 * Flags a cached secret key that was derived before a password reset.
 *
 * A password reset issues a brand new key pair server-side, but a tab that
 * already unlocked keeps its old secretKey/publicKey cached in
 * sessionStorage for the life of the tab (see the header comment above).
 * That stale key will silently and permanently fail to decrypt anything
 * sealed with the account's new key. Comparing the locally cached public key
 * against the server's current public key catches this: a mismatch means
 * the local key predates the reset.
 *
 * Either side being null means there is nothing to compare yet (no cached
 * key, or the freshness check has not resolved), which is treated as not
 * stale.
 */
export function isStaleKeyPair(local: string | null, serverCurrent: string | null): boolean {
  return local !== null && serverCurrent !== null && local !== serverCurrent;
}

interface CryptoState {
  secretKey: string | null; // base64 X25519 secret key
  publicKey: string | null; // base64 X25519 public key
  channelKeys: Record<string, string>; // channelId → base64 symmetric key
  setSecretKey: (key: string) => void;
  setPublicKey: (key: string) => void;
  setChannelKey: (channelId: string, key: string) => void;
  /** Drop one channel's legacy flat key (left, removed from, or deleted channel). */
  removeChannelKey: (channelId: string) => void;
  clearKeys: () => void;
}

export const useCryptoStore = create<CryptoState>((set) => ({
  // Hydrate from sessionStorage so an in-tab reload skips UnlockEncryption.
  secretKey: readSessionKey(SESSION_KEY_SECRET),
  publicKey: readSessionKey(SESSION_KEY_PUBLIC),
  channelKeys: {},
  setSecretKey: (key) => {
    writeSessionKey(SESSION_KEY_SECRET, key);
    set({ secretKey: key });
  },
  setPublicKey: (key) => {
    writeSessionKey(SESSION_KEY_PUBLIC, key);
    set({ publicKey: key });
  },
  setChannelKey: (channelId, key) =>
    set((state) => ({ channelKeys: { ...state.channelKeys, [channelId]: key } })),
  removeChannelKey: (channelId) =>
    set((state) => ({
      channelKeys: Object.fromEntries(
        Object.entries(state.channelKeys).filter(([id]) => id !== channelId),
      ),
    })),
  clearKeys: () => {
    // Logout path (authStore.logout → clearKeys) wipes the persisted keypair.
    removeSessionKey(SESSION_KEY_SECRET);
    removeSessionKey(SESSION_KEY_PUBLIC);
    set({ secretKey: null, publicKey: null, channelKeys: {} });
  },
}));
